"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import TrialBalancePrint from "@/components/print/TrialBalancePrint";
import DatePicker from "@/components/form/date-picker";
import Input from "@/components/form/input/InputField";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import FilterBar from "@/components/ui/FilterBar";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { rmcClient } from "@/lib/rmcClient";
import { useAppStore } from "@/lib/store";
import {
  exportTrialBalanceExcel,
  filterTrialBalanceRows,
  formatTrialBalanceAmount,
  parseTrialBalanceResponse,
  sumTrialBalanceRows,
} from "@/lib/trialBalance";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

function isoToday() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const AMOUNT_COLS = [
  "opening_debit",
  "opening_credit",
  "debit_amount",
  "credit_amount",
  "closing_debit",
  "closing_credit",
];

const DEBIT_KEYS = new Set(["opening_debit", "debit_amount", "closing_debit"]);
const CREDIT_KEYS = new Set(["opening_credit", "credit_amount", "closing_credit"]);

const AMOUNT_HEADERS = {
  opening_debit: "Opening Dr",
  opening_credit: "Opening Cr",
  debit_amount: "Debit Amount",
  credit_amount: "Credit Amount",
  closing_debit: "Closing Dr",
  closing_credit: "Closing Cr",
};

export default function RmcTrialBalance() {
  const fy = useAppStore((s) => s.activeFY);
  const printRef = React.useRef(null);
  const today = React.useMemo(() => isoToday(), []);

  const [ledgerType, setLedgerType] = React.useState("sales");
  const [partyId, setPartyId] = React.useState(null);
  const [quickSearch, setQuickSearch] = React.useState("");
  const [from, setFrom] = React.useState(fy?.start_date || "2025-04-01");
  const [to, setTo] = React.useState(today);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const [excelBusy, setExcelBusy] = React.useState(false);

  const [applied, setApplied] = React.useState({
    ledgerType: "sales",
    dateFrom: fy?.start_date || "2025-04-01",
    dateTo: today,
    partyId: null,
    accountName: "",
  });

  const { data: salesParties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: purchaseParties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });

  const partyList = ledgerType === "sales" ? salesParties : purchaseParties;

  const partySelectOptions = React.useMemo(
    () => [
      {
        id: "",
        name: `All ${ledgerType === "sales" ? "sales" : "purchase"} parties`,
      },
      ...partyList,
    ],
    [ledgerType, partyList]
  );

  const resolveAccountName = React.useCallback(
    (id) => {
      if (id == null || id === "") return "";
      const party = partyList.find((row) => String(row.id) === String(id));
      return (party?.name || "").trim();
    },
    [partyList]
  );

  React.useEffect(() => {
    if (!fy?.start_date) return;
    setFrom(fy.start_date);
    setApplied((prev) => ({
      ...prev,
      dateFrom: fy.start_date,
    }));
  }, [fy?.start_date]);

  React.useEffect(() => {
    setPartyId(null);
    setApplied((prev) => ({
      ...prev,
      ledgerType,
      partyId: null,
      accountName: "",
    }));
  }, [ledgerType]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["trialBalance", applied],
    queryFn: async () => {
      const res = await rmcClient.getTrialBalance({
        ledger_type: applied.ledgerType,
        date_from: applied.dateFrom,
        date_to: applied.dateTo,
        ...(applied.accountName ? { account_name: applied.accountName } : {}),
      });
      return parseTrialBalanceResponse(res.data);
    },
    enabled: Boolean(applied.dateFrom && applied.dateTo),
    retry: false,
  });

  React.useEffect(() => {
    if (isError) {
      const detail = error?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : error?.message || "Failed to load trial balance.";
      toast.error(msg);
    }
  }, [isError, error]);

  const displayRows = React.useMemo(
    () => filterTrialBalanceRows(data?.rows ?? [], quickSearch),
    [data?.rows, quickSearch]
  );

  const displayTotals = React.useMemo(() => {
    if (!quickSearch.trim()) return data?.totals ?? {};
    return sumTrialBalanceRows(displayRows);
  }, [data?.totals, displayRows, quickSearch]);

  const applySearch = () => {
    if (!from || !to) {
      toast.error("Please select both From and To dates.");
      return;
    }
    setApplied({
      ledgerType,
      dateFrom: from,
      dateTo: to,
      partyId,
      accountName: resolveAccountName(partyId),
    });
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      await printRef.current?.openPrint?.({
        ledgerType: applied.ledgerType,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        accountName: applied.accountName,
      });
    } catch {
      toast.error("Could not prepare PDF export");
    } finally {
      setPdfBusy(false);
    }
  };

  const exportExcel = async () => {
    if (!displayRows.length) {
      toast.error("No data to export.");
      return;
    }
    setExcelBusy(true);
    try {
      await exportTrialBalanceExcel({
        rows: displayRows,
        totals: displayTotals,
        meta: {
          ledger_type: applied.ledgerType,
          date_from: applied.dateFrom,
          date_to: applied.dateTo,
          financial_year_label: fy?.label,
        },
      });
      toast.success(`Exported ${displayRows.length} accounts`);
    } catch {
      toast.error("Failed to export Excel.");
    } finally {
      setExcelBusy(false);
    }
  };

  const columns = [
    { key: "account_name", header: "Account Name" },
    ...AMOUNT_COLS.map((key) => ({
      key,
      header: AMOUNT_HEADERS[key],
      headerClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      render: (row) => formatTrialBalanceAmount(row[key]),
    })),
  ];

  const footerCells = [
    <td key="label" className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
      Totals
    </td>,
    ...AMOUNT_COLS.map((key) => (
      <td
        key={key}
        className={`px-4 py-3 text-right font-semibold tabular-nums ${
          DEBIT_KEYS.has(key)
            ? "bg-red-500/10 text-red-400"
            : CREDIT_KEYS.has(key)
              ? "bg-green-500/10 text-green-400"
              : "text-gray-900 dark:text-white"
        }`}
      >
        {formatTrialBalanceAmount(displayTotals[key])}
      </td>
    )),
  ];

  return (
    <div className="space-y-6">
      <TrialBalancePrint ref={printRef} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageBreadCrumb pageTitle="Trial Balance" />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pdfBusy || excelBusy || isFetching}
            onClick={() => void exportPdf()}
          >
            {pdfBusy ? "Preparing…" : "Export PDF"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={excelBusy || pdfBusy || isFetching || !displayRows.length}
            onClick={() => void exportExcel()}
          >
            {excelBusy ? "Exporting…" : "Export Excel"}
          </Button>
        </div>
      </div>

      <FilterBar>
        <div className="w-full space-y-3">
          <p className="text-[10px] text-gray-500">FY: {fy?.label || "—"}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-gray-500">
                Ledger Type
              </p>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-800 dark:bg-gray-950">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    ledgerType === "sales"
                      ? "bg-white text-black shadow-sm dark:bg-white dark:text-black"
                      : "text-gray-600 hover:bg-white/80 dark:text-gray-300 dark:hover:bg-white/5"
                  }`}
                  onClick={() => {
                    setLedgerType("sales");
                    setPartyId(null);
                  }}
                >
                  Sales
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    ledgerType === "purchase"
                      ? "bg-white text-black shadow-sm dark:bg-white dark:text-black"
                      : "text-gray-600 hover:bg-white/80 dark:text-gray-300 dark:hover:bg-white/5"
                  }`}
                  onClick={() => {
                    setLedgerType("purchase");
                    setPartyId(null);
                  }}
                >
                  Purchase
                </button>
              </div>
            </div>

            <div className="min-w-[220px] flex-1 sm:min-w-[280px]">
              <SearchableSelect
                label={ledgerType === "sales" ? "Sales Party" : "Purchase Party"}
                value={partyId ?? ""}
                onChange={(v) => setPartyId(v === "" || v == null ? null : v)}
                options={partySelectOptions}
                getLabel={(o) => o.name}
                getValue={(o) => o.id}
                getSubLabel={(p) => p.gst_number}
                placeholder={`Search ${ledgerType === "sales" ? "sales" : "purchase"} party…`}
              />
            </div>

            <div>
              <p className="mb-1 text-[10px] text-gray-500">Date From</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="trial-balance-from"
                  defaultDate={from}
                  placeholder="From date"
                  onChange={(_, dateStr) => setFrom(dateStr || "")}
                />
              </div>
            </div>
            <div>
              <p className="mb-1 text-[10px] text-gray-500">Date To</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="trial-balance-to"
                  defaultDate={to}
                  placeholder="To date"
                  onChange={(_, dateStr) => setTo(dateStr || "")}
                />
              </div>
            </div>
            <div className="pb-1">
              <Button size="sm" onClick={applySearch} disabled={isFetching}>
                {isFetching ? "Loading…" : "Search"}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t border-gray-200 pt-3 dark:border-gray-800">
            <div className="min-w-[200px] flex-1">
              <p className="mb-1 text-[10px] text-gray-500">Search in results</p>
              <Input
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                placeholder="Account name…"
              />
            </div>
            <p className="pb-2 text-xs text-gray-500">
              Showing {displayRows.length} account{displayRows.length === 1 ? "" : "s"}
              {applied.dateFrom && applied.dateTo
                ? ` · ${applied.dateFrom} to ${applied.dateTo}`
                : ""}
            </p>
          </div>
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        data={displayRows}
        isLoading={isFetching}
        emptyMessage={
          isError
            ? "Trial balance could not be loaded. Ensure the API is available."
            : "No accounts for this period"
        }
        getRowKey={(row) => row.account_id ?? row.account_name}
        footer={displayRows.length > 0 ? footerCells : undefined}
      />
    </div>
  );
}
